import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { FinalQCUpdateCommand, Roll, RollSearchCommand, RollSearchResultItem } from '@app/models/knitting/roll';
import { ReassignLeftoverRollCommand } from '@app/models/production/commands/reassign-leftover-roll-command';
import { RollsReassignmentCommand } from '@app/models/production/commands/roll-reassignment-command';
import { KnittingOrder } from '@app/models/production/knitting-order';
import { getSkipperErrorHeader } from '@app/utilities/error-header.util';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class RollProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.productionApi + '/api/v1/roll';

  public createRollBatch(data: Roll[]): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(this.baseUrl + '/batch', data);
  }

  public createRoll(data: Roll): Observable<Roll> {
    return this.httpClient.post<Roll>(this.baseUrl, data);
  }

  public updateRoll(id: string, data: Roll): Observable<Roll> {
    return this.httpClient.put<Roll>(this.baseUrl + '/' + id, data);
  }

  public firstWeight(weighStationId: string, weightInLbs: number, rollId: string): Observable<ApiResponse<boolean>> {
    const payload = {
      weighStationId: weighStationId,
      weightInLbs: weightInLbs,
      rollId: rollId,
    };
    return this.httpClient.post<ApiResponse<boolean>>(`${this.baseUrl}/first-weight`, payload);
  }

  public deleteRoll(rollId: string) {
    return this.httpClient.delete<ApiResponse<Roll>>(`${this.baseUrl}/${rollId}`);
  }

  public getRolls(): Observable<Roll[]> {
    return this.httpClient.get<Roll[]>(this.baseUrl + '/all');
  }

  public getRollById(id: string): Observable<Roll> {
    return this.httpClient.get<Roll>(this.baseUrl + '/' + id);
  }

  public search(
    searchCommand: RollSearchCommand,
    pageNumber = 1,
    pageSize = 50,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<RollSearchResultItem>>> {
    const payload = {
      rollNumbers: searchCommand.rollNumbers,
    };

    let params = new HttpParams().set('pageNumber', pageNumber.toString()).set('pageSize', pageSize.toString());

    if (sortField) {
      params = params.set('sortField', sortField).set('sortOrder', sortOrder.toString());
    }

    return this.httpClient.post<ApiResponse<PaginatedApiResponse<RollSearchResultItem>>>(`${this.baseUrl}/search/paginated`, payload, {
      params,
    });
  }

  public getRollByCode(code: string, avoidInterceptor = false): Observable<Roll[]> {
    const headers = getSkipperErrorHeader(avoidInterceptor);
    return this.httpClient.post<Roll[]>(
      `${this.baseUrl}/by-knitting-order/`,
      {
        number: code,
      },
      { headers },
    );
  }

  public getRollsByKnittingOrder(id: string, avoidInterceptor = false, lightweight = false): Observable<ApiResponse<Roll[]>> {
    const headers = getSkipperErrorHeader(avoidInterceptor);
    let params = new HttpParams();
    if (lightweight) params = params.set('lightweight', 'true');
    return this.httpClient.get<ApiResponse<Roll[]>>(this.baseUrl + '/by-knitting-order/' + id, { headers, params });
  }

  public getRollsByAssetId(assetId: string): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.get<ApiResponse<Roll[]>>(this.baseUrl + '/weigh-station/' + assetId);
  }

  public getKnittingOrderByAssetId(assetId: string): Observable<ApiResponse<KnittingOrder[]>> {
    return this.httpClient.post<ApiResponse<KnittingOrder[]>>(`${this.baseUrl}/knitting-machine/knitting-orders`, [assetId]);
  }

  public deleteRollsByBatch(rolls: string[]): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.delete<ApiResponse<Roll[]>>(`${this.baseUrl}/roll-number/batch`, { body: rolls });
  }

  public getRollsByWorkOrder(workOrderReference: string, avoidInterceptor = false): Observable<ApiResponse<Roll[]>> {
    const headers = getSkipperErrorHeader(avoidInterceptor);
    return this.httpClient.get<ApiResponse<Roll[]>>(`${this.baseUrl}/${workOrderReference}/production-card-rolls`, { headers });
  }

  public getCandidateRollsByWorkOrder(workOrderReference: string, targetProductionCardId: string): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/by-work-order/candidate-rolls`, {
      workOrderReference,
      targetProductionCardId,
    });
  }

  public rollSplit(stationAssetId: string, originalRollNumber: string, newRollWeightInLbs: number): Observable<ApiResponse<Roll[]>> {
    //add via query params
    const params = new HttpParams()
      .set('stationAssetId', stationAssetId)
      .set('originalRollNumber', originalRollNumber)
      .set('newRollWeightInLbs', newRollWeightInLbs.toString());

    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/split`, {}, { params });
  }

  public getRollsByNumbers(rollNumbers: string[]): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/batch/numbers`, rollNumbers);
  }

  /** `rollNumber` is matched as literal text the roll number contains (case-insensitive), not as a regex. */
  public searchByRollNumber(rollNumber: string): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/search`, { rollNumber });
  }

  public setFinalWeight(rollNumber: string, finalWeightInLbs: number, weightStationAssetId: string): Observable<ApiResponse<Roll>> {
    const params = new HttpParams().set('finalWeightInLbs', finalWeightInLbs.toString()).set('weightStationAssetId', weightStationAssetId);
    return this.httpClient.post<ApiResponse<Roll>>(`${this.baseUrl}/number/${encodeURIComponent(rollNumber)}/final-weight`, {}, { params });
  }

  public getRollsCurrentlyLinkedToProductionCardNumber(productionCardNumber: string): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.get<ApiResponse<Roll[]>>(
      `${this.baseUrl}/production-card/${encodeURIComponent(productionCardNumber)}/current-linked-rolls`,
    );
  }

  public finalQc(
    rollNumber: string,
    isApproved: boolean,
    comments?: string,
    finalGsm?: number,
    finalWidth?: number,
  ): Observable<ApiResponse<Roll>> {
    return this.httpClient.post<ApiResponse<Roll>>(`${this.baseUrl}/final-qc`, {
      rollNumber,
      isApproved,
      comments: comments || '',
      finalGsm,
      finalWidth,
    });
  }

  public batchFinalQc(commands: FinalQCUpdateCommand[]): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/final-qc/batch`, commands);
  }

  public getRollsAvailableRollsOFOtherWorkOrder(command: {
    workOrderReference: string;
    targetProductionCardId: string;
    targetFabricCode?: string;
  }): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/by-work-order/candidate-rolls`, command);
  }

  public reassignRolls(command: RollsReassignmentCommand): Observable<ApiResponse<Roll[]>> {
    return this.httpClient.post<ApiResponse<Roll[]>>(`${this.baseUrl}/reassign`, command);
  }

  public reassignLeftover(command: ReassignLeftoverRollCommand): Observable<ApiResponse<Roll>> {
    return this.httpClient.post<ApiResponse<Roll>>(`${this.baseUrl}/reassign-leftover`, command);
  }
}
